import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { MainLayout } from './layout/main-layout/main-layout';
import { CropMonitoringSummaryComponent } from '@app/pages/crop-monitoring/crop-monitoring-summary/crop-monitoring-summary';
import { RequestsPageComponent } from '@app/pages/crop-monitoring/pages/request/requests-page';
import { RequestAddPageComponent } from '@app/pages/crop-monitoring/components/request-add-page/request-add-page';
import { MobileTemplatesPageComponent } from './pages/mobile-templates-page/mobile-templates-page';
import { TaskSchedulerPageComponent } from '@app/pages/admin/task-scheduler-page/task-scheduler-page';
import { UserManagementPageComponent } from '@app/pages/user-management/user-management-page/user-management-page';
import { ElectronicFieldMapPageComponent } from '@app/pages/admin/administration-summary/administration-summary';
import { LoggingPageComponent } from '@app/pages/admin/logging-page/logging-page';
import { AppealsPageComponent } from '@app/pages/admin/appeals-page/appeals-page';
import { SystemStatusPageComponent } from '@app/pages/admin/system-status-page/system-status-page';
import { FileStorageManagementPageComponent } from './pages/file-storage-management-page/file-storage-management-page';
import { SystemDocumentationPageComponent } from '@app/pages/admin/system-documentation-page/system-documentation-page';
import { SystemDocumentationBlocksPageComponent } from '@app/pages/admin/system-documentation-blocks-page/system-documentation-blocks-page';
import { DataManagementPageComponent } from '@app/pages/admin/data-management-page/data-management-page';
import { NotFoundComponent } from './pages/not-found/not-found';
import { UnderDevelopmentComponent } from '@components/under-development/under-development.component';
import { ApiTestComponent } from '@components/api-test/api-test.component';
import { CallbackComponent } from '@components/callback/callback.component';
import { authCheckGuard } from './guards/auth-check.guard';
import { AdminGuard } from './guards/admin.guard';
import { AuthErrorComponent } from '@components/auth-error/auth-error.component';
import { LoginPageComponent } from './pages/login-page/login-page';
import { MainMenuComponent } from '@app/pages/main-menu/main-menu.component';
import { PassportMenuGeneral } from '@app/pages/electronic-field-map/pages/fields/passport/menu/passport-menu-general/passport-menu-general';
import { DataTableDemoComponent } from '@app/pages/data-table-demo/data-table-demo.component';
import { UserSupportTicketsPageComponent } from './pages/user-support-tickets-page/user-support-tickets-page.component';
import { TileMap } from '@app/pages/weather/pages/tile-map/tile-map';
import { WeatherLoggingComponent } from './pages/weather-page/weather-logging/weather-logging.component';
import { WeatherAdministrationMenuComponent } from './pages/weather-page/weather-administration-menu/weather-administration-menu.component';
import { WeatherMainMenuComponent } from '@app/pages/weather/weather-main-menu.component';
import { ChatComponent } from './pages/chat-component/chat.component';
import { SatelliteImagesComponent } from './pages/satellite-images/satellite-images.component';
import { SatelliteImagesAdminComponent } from './pages/satellite-images-admin/satellite-images-admin.component';
import { SatelliteDataComponent } from './pages/satellite-data/satellite-data.component';
import { UserChatComponent } from './pages/user-support-tickets-page/user-chat.component';
import { DocumentationPageComponent } from './pages/help/documentation-page/documentation-page';
import { UavDataComponent } from './pages/uav-data-page/uav-data.component';
import { GisPageRefactoredNewComponent } from '@app/pages/weather/pages/gis-weather/gis-page.component.refactored.new';
import { ProfilePageComponent } from './pages/profile-page/profile-page.component';
import { CropMonitoringSatelliteComponent } from './pages/crop-monitoring-satellite/crop-monitoring-satellite.component';
import { GisRequestComponent } from '@app/pages/crop-monitoring/gis-request/gis-request';
import { CropYieldForecastsPageComponent } from './pages/crop-yield-forecasts-page/crop-yield-forecasts-page.component';
import { CropIdentificationMainComponent } from '@app/pages/crop-identification/crop-identification-main.component';
import { GisCropIdentification } from '@app/pages/crop-identification/pages/gis-crop-identification/gis-crop-identification';
import { CropIdentificationList } from '@app/pages/crop-identification/pages/crop-identification-list/crop-identification-list';
import { CropYieldForecastListComponent } from './pages/crop-yield-forecasts-page/crop-yield-forecast-list/crop-yield-forecast-list.component';
import { CropYieldAnalysisComponent } from './pages/crop-yield-forecasts-page/crop-yield-analysis/crop-yield-analysis.component';
import { FieldsIdentificationPageComponent } from './pages/fields-identification-page/fields-identification.component';
import { FieldsIdentificationListComponent } from './pages/fields-identification-page/fields-identification-list/fields-identification-list.component';
import { FieldsIdentificationMapComponent } from './pages/fields-identification-page/fields-identification-map/fields-identification-map.component';
import { CropMonitoringAdministrationMenuComponent } from './pages/crop-monitoring-page/crop-monitoring-administration-menu/crop-monitoring-administration-menu.component';
import { AnomaliesMapPageComponent } from '@app/pages/crop-monitoring/pages/anomalies/anomalies-map-page';
import {CropMonitoringPageComponent} from '@app/pages/crop-monitoring-page/crop-monitoring-page';
import {FarmerRecommendationsPageComponent} from '@app/pages/crop-monitoring/pages/farmer-recommendations/farmer-recommendations-page';

const routes: Routes = [
  {
    path: 'login',
    component: LoginPageComponent,
  },
  {
    path: 'callback',
    component: CallbackComponent,
  },
  {
    path: 'auth-error',
    component: AuthErrorComponent,
  },
  {
    path: 'ui-kit',
    component: DataTableDemoComponent,
  },
  {
    path: '',
    component: MainLayout,
    canActivate: [authCheckGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'main-menu',
      },
      {
        path: 'crop-monitoring',
        component: CropMonitoringPageComponent,
        data: { breadcrumb: 'Crop monitoring', title: 'Crop monitoring: Main page' },
        children: [
          {
            path: '',
            component: CropMonitoringSummaryComponent,
            data: { breadcrumb: '', title: 'Crop monitoring: Main page' },
          },
          {
            path: 'gis',
            component: GisRequestComponent,
          },
          {
            path: 'requests',
            component: RequestsPageComponent,
            data: { breadcrumb: 'Bids', title: 'Crop monitoring: Bids' },
            children: [
              {
                path: 'add',
                component: RequestAddPageComponent,
                data: { breadcrumb: 'Add request', title: 'Crop monitoring: Add request' },
              },
              {
                path: 'edit/:id',
                component: RequestAddPageComponent,
                data: { breadcrumb: 'Edit request', title: 'Crop monitoring: Edit request' },
              },
            ],
          },
          {
            path: 'anomalies-map',
            component: AnomaliesMapPageComponent,
            data: {
              breadcrumb: 'ANOMALIES_MAP_PAGE_TITLE',
              title: 'Crop monitoring: Anomaly map',
            },
          },
          {
            path: 'mobile-templates',
            component: MobileTemplatesPageComponent,
            data: { breadcrumb: 'Mobile templates', title: 'Mobile templates' },
          },
          {
            path: 'satellite-images',
            component: CropMonitoringSatelliteComponent,
            data: {
              breadcrumb: 'SATELLITE_IMAGES_AND_INDICES_MOSAIC',
              title: 'SATELLITE_IMAGES_AND_INDICES_MOSAIC',
            },
          },
          {
            path: 'recommendations-for-farmers',
            component: FarmerRecommendationsPageComponent,
            data: {
              breadcrumb: 'Recommendations for farmers',
              title: 'Recommendations for farmers',
            },
          },
          {
            path: 'users',
            component: UnderDevelopmentComponent,
            data: { breadcrumb: 'Users', title: 'Crop monitoring: Users' },
          },
          {
            path: 'administration',
            component: CropMonitoringAdministrationMenuComponent,
            canActivate: [AdminGuard],
            data: { breadcrumb: 'Administration', title: 'Crop Monitoring Administration' },
            children: [
              {
                path: 'scheduler',
                component: TaskSchedulerPageComponent,
                data: {
                  breadcrumb: 'Task Scheduler',
                  title: 'Task Scheduler',
                  filterKeywords: ['SatelliteIndices'],
                  parentRoute: '/crop-monitoring/administration',
                },
              },
              {
                path: 'logging',
                component: LoggingPageComponent,
                data: {
                  breadcrumb: 'Logging',
                  title: 'Crop Monitoring: Logging',
                  filterKeywords: ['CropMonitoring'],
                  parentRoute: '/crop-monitoring/administration',
                },
              },
            ],
          },
        ],
      },
      {
        path: 'dictionaries',
        loadChildren: () =>
          import('@app/pages/dictionaries-page/dictionaries.routes').then((m) => m.dictionariesRoutes),
        data: { breadcrumb: 'Directories', title: 'Directories' },
      },
      {
        path: 'electronic-field-map',
        loadChildren: () =>
          import('@app/pages/electronic-field-map/electronic-fields-map.routing').then(
            (m) => m.ElectronicFieldsMapRouting,
          ),
        data: { breadcrumb: 'Electronic Field Maps', title: 'Electronic Field Maps' },
      },
      {
        path: 'main-menu',
        component: MainMenuComponent,
        data: { breadcrumb: 'Main menu', title: 'Main menu' },
      },
      {
        path: 'weather',
        data: { breadcrumb: 'Weather' },
        children: [
          {
            path: '',
            component: WeatherMainMenuComponent,
            data: { title: 'Weather', breadcrumb: null },
          },
          {
            path: 'gis',
            component: GisPageRefactoredNewComponent,
            data: { breadcrumb: 'GIS', title: 'GIS' },
          },
          {
            path: 'tile-map',
            component: TileMap,
            data: { breadcrumb: 'Tile map', title: 'Tile map' },
          },
          {
            path: 'administration',
            component: WeatherAdministrationMenuComponent,
            canActivate: [AdminGuard],
            data: { breadcrumb: 'Administration', title: 'Weather Administration' },
            children: [
              {
                path: 'scheduler',
                component: TaskSchedulerPageComponent,
                data: {
                  breadcrumb: 'Task Scheduler',
                  title: 'Task Scheduler',
                  filterKeywords: ['Era', 'IBM', 'Ibm', 'Ugm'],
                  parentRoute: '/weather/administration',
                },
              },
              {
                path: 'data-processing',
                component: WeatherLoggingComponent,
                data: { breadcrumb: 'ProcessedDataProtocol', title: 'Weather: ProcessedDataProtocol' },
              },
              {
                path: 'logging',
                component: LoggingPageComponent,
                data: {
                  breadcrumb: 'Logging',
                  title: 'Weather Administration: Logging',
                  filterKeywords: ['Era', 'IBMWeather', 'Ugm', 'Weather'],
                  parentRoute: '/weather/administration',
                },
              },
            ],
          },
        ],
      },
      {
        path: 'gis',
        redirectTo: 'weather/gis',
        pathMatch: 'full',
      },
      {
        path: 'tile-map',
        redirectTo: 'weather/tile-map',
        pathMatch: 'full',
      },
      {
        path: 'admin',
        data: { breadcrumb: 'Administration', title: 'Administration' },
        canActivate: [AdminGuard],
        children: [
          {
            path: '',
            component: ElectronicFieldMapPageComponent,
            data: { breadcrumb: null },
          },
          {
            path: 'task-scheduler',
            component: TaskSchedulerPageComponent,
            data: {
              breadcrumb: 'Task Scheduler',
              title: 'Administration: Task Scheduler',
              parentRoute: '/admin',
            },
          },
          {
            path: 'user-management',
            component: UserManagementPageComponent,
            canActivate: [AdminGuard],
            data: { breadcrumb: 'User Management', title: 'Administration: User Management' },
          },
          {
            path: 'logging',
            component: LoggingPageComponent,
            data: { breadcrumb: 'Logging', title: 'Administration: Logging' },
          },
          {
            path: 'system-status',
            component: SystemStatusPageComponent,
            data: { breadcrumb: 'System Status', title: 'Administration: System Status' },
          },
          {
            path: 'appeals',
            component: AppealsPageComponent,
            data: { breadcrumb: 'Appeals', title: 'Admin: Appeals List' },
          },
          {
            path: 'appeals/chat',
            component: ChatComponent,
            data: { breadcrumb: 'Chats', title: 'Appeals Chats' },
          },
          {
            path: 'appeals/chat/:id',
            component: ChatComponent,
            data: { breadcrumb: 'Chats', title: 'Support Chats' },
          },
          {
            path: 'file-storage',
            component: FileStorageManagementPageComponent,
            data: { breadcrumb: 'File Storage Management', title: 'Admin: File Storage Management' },
          },
          {
            path: 'data-management',
            component: DataManagementPageComponent,
            data: { breadcrumb: 'Data Management', title: 'Admin: Data Management' },
          },
          {
            path: 'system-documentation',
            component: SystemDocumentationPageComponent,
            data: { breadcrumb: 'System Documentation', title: 'Administration: System Documentation' },
          },
          {
            path: 'system-documentation-blocks',
            component: SystemDocumentationBlocksPageComponent,
            data: { breadcrumb: 'System Documentation Blocks', title: 'Administration: System Documentation Blocks' },
          },
        ],
      },
      {
        path: 'notifications',
        component: UnderDevelopmentComponent,
        data: { breadcrumb: 'Notifications', title: 'Notifications' },
      },
      {
        path: 'soil-composition',
        component: UnderDevelopmentComponent,
        data: { breadcrumb: 'Soil composition', title: 'Soil composition' },
      },
      {
        path: 'uav-data',
        component: UavDataComponent,
        data: { breadcrumb: 'UAV data', title: 'UAV data' },
      },
      {
        path: 'satellite-data',
        data: { breadcrumb: 'Satellite Data', title: 'Satellite Data' },
        children: [
          {
            path: '',
            component: SatelliteDataComponent,
            data: { breadcrumb: null },
          },
          {
            path: 'admin',
            component: SatelliteDataComponent,
            canActivate: [AdminGuard],
            data: { breadcrumb: 'Administration' },
          },
          {
            path: 'scheduler',
            component: TaskSchedulerPageComponent,
            canActivate: [AdminGuard],
            data: {
              breadcrumb: 'Task Scheduler',
              title: 'Task Scheduler',
              filterKeywords: ['Sentinel', 'Landsat'],
              parentRoute: '/satellite-data/admin',
            },
          },
        ],
      },
      {
        path: 'satellite-images',
        component: SatelliteImagesComponent,
        data: { breadcrumb: 'Satellite Images', title: 'Satellite Images' },
      },
      {
        path: 'satellite-images-admin',
        component: SatelliteImagesAdminComponent,
        canActivate: [AdminGuard],
        data: { breadcrumb: 'Satellite images admin', title: 'Satellite Images Admin' },
      },
      {
        path: 'map',
        component: UnderDevelopmentComponent,
        data: { breadcrumb: 'Map', title: 'Map' },
      },
      {
        path: 'crop-yield-forecasts',
        data: { breadcrumb: 'Crop Yield Forecasts', title: 'Crop Yield Forecasts' },
        children: [
          {
            path: '',
            component: CropYieldForecastsPageComponent,
            data: { breadcrumb: null },
          },
          {
            path: 'list',
            component: CropYieldForecastListComponent,
            data: { breadcrumb: 'List', title: 'Forecast List' },
          },
          {
            path: 'analysis',
            component: CropYieldAnalysisComponent,
            data: { breadcrumb: 'Analysis', title: 'Yield Analysis' },
          },
        ],
      },
      {
        path: 'fields-identification',
        data: { breadcrumb: 'Fields identification', title: 'Fields identification' },
        children: [
          {
            path: '',
            component: FieldsIdentificationPageComponent,
            data: { breadcrumb: null },
          },
          {
            path: 'list',
            component: FieldsIdentificationListComponent,
            data: { breadcrumb: 'Boundaries List', title: 'Boundaries List' },
          },
          {
            path: 'gis',
            component: FieldsIdentificationMapComponent,
            data: { breadcrumb: 'GIS', title: 'GIS' },
          },
        ],
      },
      {
        path: 'crop-identification',
        data: { breadcrumb: 'Crop identification', title: 'Crop identification' },
        children: [
          {
            path: '',
            component: CropIdentificationMainComponent,
            data: { breadcrumb: 'Crop identification', title: 'Crop identification' },
          },
          {
            path: 'gis',
            component: GisCropIdentification,
            data: { breadcrumb: 'GIS', title: 'GIS' },
          },
          {
            path: 'list',
            component: CropIdentificationList,
            data: { breadcrumb: 'Crop identification', title: 'Crop identification' },
          },
        ],
      },
      {
        path: 'reports',
        component: UnderDevelopmentComponent,
        data: { breadcrumb: 'Reports', title: 'Reports' },
      },
      {
        path: 'fields-passport',
        component: UnderDevelopmentComponent,
        data: { breadcrumb: 'fields-passport', title: 'fields-passport' },
      },
      {
        path: 'upload-images',
        redirectTo: 'electronic-field-map/upload-images',
        pathMatch: 'full',
      },
      {
        path: 'field',
        component: PassportMenuGeneral,
        data: { breadcrumb: 'fields', title: 'fields' },
      },
      {
        path: 'services',
        component: UnderDevelopmentComponent,
        data: { breadcrumb: 'Services', title: 'Services' },
      },
      {
        path: 'profile',
        component: ProfilePageComponent,
        data: { breadcrumb: 'Profile', title: 'Личный кабинет' },
      },
      {
        path: 'settings',
        component: UnderDevelopmentComponent,
        data: { breadcrumb: 'Settings', title: 'Settings' },
      },
      {
        path: 'users',
        component: UnderDevelopmentComponent,
        data: { breadcrumb: 'Users', title: 'Users' },
      },
      {
        path: 'api-test',
        component: ApiTestComponent,
        data: { breadcrumb: 'API Test', title: 'API Configuration Test' },
      },
      {
        path: 'help',
        children: [
          {
            path: 'documentation',
            component: DocumentationPageComponent,
            data: { breadcrumb: 'Electronic Documentation', title: 'Electronic Documentation' },
          },
          {
            path: 'support-tickets',
            component: UserSupportTicketsPageComponent,
            data: { breadcrumb: 'Support Tickets', title: 'Support Tickets' },
          },
          {
            path: 'support-tickets/chat/:id',
            component: UserChatComponent,
            data: { breadcrumb: 'Chats', title: 'Support Chats' },
          },
        ],
      },
    ],
  },
  {
    path: '**',
    component: NotFoundComponent,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
